import { useLayoutEffect, useRef, useState } from "react";
import Button from "../../models/Button";
import KnxProperty from "../KnxProperty";

import { createPortal } from "react-dom";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRedraw } from "../../models/useRerender";
import { PanelCell } from "./PanelLayoutTypes";
import ButtonChannel from "../../models/ButtonChannel";
import PanelUtils from "./PanelUtils";
import { PanelConfig, ExtCellProperty, ExtPageProperty } from "./PanelConfig";
import { itx, noItx } from "../../translationItx/itx";
import { ChannelTypeElement } from "../KnxButtonTable/OneButton";

function OneExtProperty(props: {
  panel: Button;
  buttonChannel?: ButtonChannel | undefined;
  panelMappingConfig: PanelConfig;
  pageIndex: number;
  cell?: PanelCell;
  extProperty: ExtCellProperty | ExtPageProperty;
}) {
  const { panel, panelMappingConfig, pageIndex, cell, extProperty, buttonChannel } = props;
  const redraw = useRedraw();

  let label = "label"; // no itx
  let id = "";
  let instanceId = buttonChannel?.knxChannel?.instanceId || "";

  if (cell) {
    let buttonChannel: ButtonChannel | undefined = panel.buttonChannels.getItemByTag(cell.functionTag || "");

    if (!buttonChannel) {
      return <div>{itx("ExtPropeties.noButtonChannel", "No button channel")}</div>;
    }

    const functionNumber = buttonChannel ? parseInt(buttonChannel.tag.substring(1)) : 0;
    if (extProperty.id) {
      id = extProperty.id;
      const valueExt = panel.knxInstanceData.getExtendedData(id, instanceId);
      label = valueExt?.info?.text || "";
    } else {
      if (!(extProperty as ExtCellProperty).idToProperty) {
        return <div>{noItx("No idToProperty")}</div>;
      }

      let res: any;
      try {
        res = (extProperty as ExtCellProperty).idToProperty?.({
          func: buttonChannel,
          funcNumber: functionNumber,
          page: pageIndex,
          idx: cell.idx,
          column: cell.column,
          row: cell.row,
        });
      } catch (error: any) {
        return <div>Error: {error.message}</div>;
      }

      id = res?.id;
      instanceId = res?.instanceId;
    }

    if (!id) {
      return <div>{itx("ExtPropeties.noId", "No id")}</div>;
    }

    if (!instanceId) {
      try {
        instanceId = panelMappingConfig.pageInstanceId?.(pageIndex) || "";
      } catch (error: any) {
        return <div>Error: {error.message}</div>;
      }
    }
  } else {
    id = (extProperty as ExtPageProperty).id;
    if (pageIndex >= 0) {
      instanceId = panelMappingConfig.pageInstanceId?.(pageIndex) || "";
    }
  }

  if (!panel.knxInstanceData.getUiReferenced(id, instanceId)) {
    return null;
  }

  const item = panel.template?.knxProductData.deref(id);
  if (!item) {
    return <div>{itx("ExtPropeties.noItem", "No item")}</div>;
  }

  label = panel.template?.knxProductData.getTranslatedProperty("Text", item) || "";

  return (
    <KnxProperty
      className="extProperty"
      device={panel}
      data={item}
      label={label}
      instanceId={instanceId || ""}
      moduleId=""
      parameterId={id}
      onChange={async () => {
        if (panel.reEvalKnxMetadataNeeded) {
          PanelUtils.markAsManagedByEstada(panel, id, instanceId);
          await panel.updateAllFromKnxMetadataImmediately();
          redraw();
        }
      }}
    />
  );
}

export default function ExtProperties(props: {
  title: string;
  panel: Button;
  panelMappingConfig: PanelConfig;
  extProperties: Array<ExtCellProperty | ExtPageProperty> | undefined;
  pageIndex: number;
  cell?: PanelCell | undefined;
  onClose: () => void;
}) {
  const { title, panel, panelMappingConfig, extProperties, pageIndex, cell, onClose } = props;
  const redraw = useRedraw();

  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!extProperties) return;

    function updatePosition() {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({ top: rect.top - 25, right: window.innerWidth - rect.right - 5 });
    }

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [extProperties]);

  const buttonChannel = cell ? panel.buttonChannels.getItemByTag(cell.functionTag) || undefined : undefined;

  const extPropertyElements: Array<JSX.Element> = [];
  extProperties?.forEach((extProperty, index) => {
    const elm = OneExtProperty({
      panel: panel,
      buttonChannel: buttonChannel,
      panelMappingConfig: panelMappingConfig,
      pageIndex: pageIndex,
      cell: cell,
      extProperty: extProperty,
    });

    if (elm) {
      extPropertyElements.push(elm);
    }
  });

  return (
    <>
      <span ref={anchorRef} style={{ display: "inline-block", width: 0, height: 0 }} />
      {pos &&
        createPortal(
          <div
            className="Theme estadaColumn left"
            style={{
              position: "fixed",
              top: pos.top,
              right: pos.right,
              display: "block",
              // width: "300px",
              zIndex: 100,
              backgroundColor: "white",
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
              borderRadius: "10px",
              boxShadow: "var(--flyout-shadow)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                position: "absolute",
                left: "-1000px",
                top: "-1000px",
                width: "2000px",
                minHeight: "2000px",
                backgroundColor: "transparent",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            />

            <div
              className="estadaRow left w100"
              style={{
                height: "40px",
                paddingLeft: "10px",
                paddingRight: "2px",
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                backgroundColor: "var(--backgroundColorGrey)",
                color: "white",
              }}
            >
              {title}

              <div style={{ flex: 1 }}></div>

              <div
                className="button circle"
                style={{
                  position: "relative",
                  width: "30px",
                  height: "30px",
                  cursor: "pointer",
                  backgroundColor: "var(--primary-color)",
                }}
                onClick={() => onClose()}
              >
                <FontAwesomeIcon icon={faXmark} color="white" />
              </div>
            </div>

            <div className="estadaColumn left" style={{ padding: "10px" }}>
              {/* {buttonChannel && <ChannelTypeElement channel={buttonChannel} device={panel} />} */}

              {extPropertyElements.length > 0 ? (
                <table className="noCellPadding" style={{ borderCollapse: "separate", borderSpacing: "0 5px" }}>
                  <tbody>{extPropertyElements}</tbody>
                </table>
              ) : (
                <div>{itx("ExtPropeties.noProperties", "Keine Eigenschaften für diese Zelle")}</div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
